import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lightning, Plus, Trash, ArrowsClockwise, Warning, CheckCircle,
  EnvelopeSimple, FolderSimple, Envelope, Calendar, ToggleLeft, ToggleRight,
  Clock, Sparkle, X, CaretRight, SealWarning, Globe, Target, Sliders, ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  getAutomations, createAutomation, updateAutomation, toggleAutomation,
  deleteAutomation, getConnectorStatus,
} from "../api/automations";
import { getProjects, getRoutingAccuracyStats, replayIntake } from "../api/projects";
import { getRouterThresholds, updateRouterThresholds } from "../api/emailRouting";
import { useAuth } from "../contexts/AuthContext";
import AutomationDetailModal from "../components/automations/AutomationDetailModal";
import AutomationsSection from "../components/automations/AutomationsSection";

// ── Template catalogue ─────────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id: "scheduled_email",
    label: "Scheduled Email",
    description: "Send a custom email to any recipient on a recurring schedule — daily, weekly or monthly.",
    icon: EnvelopeSimple,
    requires: ["outlook"],
    color: "blue",
  },
  {
    id: "file_upload_alert",
    label: "New File Upload Alert",
    description: "Get notified by email whenever a new file is uploaded to a project folder in OneDrive.",
    icon: FolderSimple,
    requires: ["outlook", "onedrive"],
    color: "emerald",
  },
  {
    id: "email_received_alert",
    label: "New Email Received",
    description: "When a new email lands in your Inbox, save the email and its attachments to a chosen OneDrive folder.",
    icon: Envelope,
    requires: ["outlook", "onedrive"],
    color: "violet",
  },
];

const TEMPLATE_MAP = Object.fromEntries(TEMPLATES.map((t) => [t.id, t]));

const FREQUENCY_OPTIONS = [
  { value: "Day",   label: "Daily" },
  { value: "Week",  label: "Weekly" },
  { value: "Month", label: "Monthly" },
];
const WEEKDAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => ({ value: i, label: `${String(i).padStart(2,"0")}:00` }));

// ── Colour helpers ─────────────────────────────────────────────────────────────
const COLOR = {
  blue:    { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-600",    pill: "bg-blue-100 text-blue-700" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", pill: "bg-emerald-100 text-emerald-700" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-600",  pill: "bg-violet-100 text-violet-700" },
};

// ── Small helpers ──────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 60000);
    if (diff < 1)  return "just now";
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.round(diff/60)}h ago`;
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch { return iso; }
}

function RunStatus({ run }) {
  if (!run) return <span className="text-[10px] text-slate-400">No runs yet</span>;
  const map = { Succeeded: "text-emerald-600", Failed: "text-red-500", Running: "text-blue-500" };
  return (
    <span className={`text-[10px] font-medium ${map[run.status] || "text-slate-400"}`}>
      {run.status} · {fmtDate(run.start_time)}
    </span>
  );
}

// ── Create automation wizard ───────────────────────────────────────────────────
function CreateModal({ connectorStatus, projects, userEmail, onCreated, onClose, onNavigateToIntegrations }) {
  const [step, setStep] = useState(1);         // 1 template | 2 config | 3 scope
  const [selectedTemplate, setTemplate]  = useState(null);
  const [form, setForm] = useState({
    name: "", recipient_email: userEmail || "", subject: "", body: "",
    frequency: "Week", interval: 1, week_days: ["Monday"], hour: 9, minute: 0,
    folder_id: "", folder_path: "", project_name: "",
    destination_folder_id: "", destination_folder_name: "", only_with_attachments: true,
  });
  const [scope, setScope]         = useState("all");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [saving, setSaving]       = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canUseTemplate = (tmpl) => {
    if (tmpl.requires.includes("outlook") && !connectorStatus?.outlook?.connected) return false;
    if (tmpl.requires.includes("onedrive") && !connectorStatus?.onedrive?.connected) return false;
    return true;
  };

  const isEmailReceived = selectedTemplate === "email_received_alert";
  const totalSteps      = isEmailReceived ? 2 : 3;

  // Reset to step 1 whenever the user picks a different template
  useEffect(() => { setStep(1); }, [selectedTemplate]);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Automation name is required.");
      return;
    }
    if (!isEmailReceived && !form.recipient_email.trim()) {
      toast.error("Recipient email is required.");
      return;
    }
    if (isEmailReceived && !form.destination_folder_id.trim()) {
      toast.error("Destination OneDrive folder ID is required.");
      return;
    }
    setSaving(true);
    try {
      const config = {
        ...((!isEmailReceived) && { recipient_email: form.recipient_email }),
        ...(selectedTemplate === "scheduled_email" && {
          subject: form.subject,
          body: form.body,
          frequency: form.frequency,
          interval: form.interval,
          week_days: form.frequency === "Week" ? form.week_days : undefined,
          hour: form.hour,
          minute: form.minute,
        }),
        ...(selectedTemplate === "file_upload_alert" && {
          folder_id: form.folder_id,
          folder_path: form.folder_path,
          project_name: form.project_name,
        }),
        ...(isEmailReceived && {
          destination_folder_id: form.destination_folder_id,
          destination_folder_name: form.destination_folder_name,
          only_with_attachments: form.only_with_attachments,
        }),
      };
      await createAutomation({
        name: form.name,
        description: TEMPLATE_MAP[selectedTemplate]?.description || "",
        template_type: selectedTemplate,
        config,
        // Email-received automations are inherently global (Inbox-scoped),
        // so we always store project_scope=all for them.
        project_scope: isEmailReceived ? "all" : scope,
        project_ids: !isEmailReceived && scope === "specific" ? selectedProjects : [],
        user_email: userEmail,
      });
      toast.success("Automation created!");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create automation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="create-automation-modal">
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Lightning size={15} className="text-primary" weight="fill" />
            <span className="font-semibold text-slate-900 text-sm">New Automation</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              {Array.from({ length: totalSteps }, (_, i) => i + 1).map(n => (
                <div key={n} className={`w-5 h-1.5 rounded-full transition-colors ${step >= n ? "bg-primary" : "bg-slate-200"}`} />
              ))}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-modal-btn"><X size={15}/></button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5">

          {/* Step 1 — pick template */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Choose a template</p>
              {TEMPLATES.map(tmpl => {
                const Icon = tmpl.icon;
                const c    = COLOR[tmpl.color];
                const ok   = canUseTemplate(tmpl);
                return (
                  <button key={tmpl.id} onClick={() => setTemplate(tmpl.id)}
                    className={`w-full text-left p-4 border rounded transition-all ${
                      selectedTemplate === tmpl.id
                        ? `${c.bg} ${c.border} border-2`
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`template-${tmpl.id}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${c.bg} border ${c.border}`}>
                        <Icon size={15} className={c.text} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{tmpl.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{tmpl.description}</p>
                        {!ok && (
                          <p className="text-[10px] text-amber-600 mt-1 font-medium flex items-center gap-1">
                            <SealWarning size={10}/> Connector not set up —{" "}
                            <button onClick={onNavigateToIntegrations}
                              className="underline hover:text-amber-700">go to Integrations</button>
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2 — configure */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configure</p>

              <Field label="Automation name" required>
                <input value={form.name} onChange={e => set("name", e.target.value)}
                  placeholder="e.g. Weekly drawing reminder" data-testid="form-name"
                  className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </Field>

              {!isEmailReceived && (
                <Field label="Send email to" required>
                  <input value={form.recipient_email} onChange={e => set("recipient_email", e.target.value)}
                    placeholder="engineer@company.com" type="email" data-testid="form-recipient"
                    className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                </Field>
              )}

              {selectedTemplate === "scheduled_email" && (
                <>
                  <Field label="Subject">
                    <input value={form.subject} onChange={e => set("subject", e.target.value)}
                      placeholder="Weekly Project Update" data-testid="form-subject"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </Field>
                  <Field label="Email body (HTML supported)">
                    <textarea value={form.body} onChange={e => set("body", e.target.value)}
                      rows={4} placeholder="<p>Hello, this is your weekly reminder...</p>"
                      data-testid="form-body"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y" />
                  </Field>
                  <Field label="Schedule">
                    <div className="flex gap-2 flex-wrap">
                      <select value={form.frequency} onChange={e => set("frequency", e.target.value)}
                        className="text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none">
                        {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {form.frequency === "Week" && (
                        <select value={form.week_days[0]} onChange={e => set("week_days", [e.target.value])}
                          className="text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none">
                          {WEEKDAYS.map(d => <option key={d}>{d}</option>)}
                        </select>
                      )}
                      <select value={form.hour} onChange={e => set("hour", Number(e.target.value))}
                        className="text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none">
                        {HOURS.map(h => <option key={h.value} value={h.value}>{h.label} AEST</option>)}
                      </select>
                    </div>
                  </Field>
                </>
              )}

              {selectedTemplate === "file_upload_alert" && (
                <>
                  <Field label="Project name (OneDrive folder to monitor)" required>
                    <input value={form.project_name} onChange={e => set("project_name", e.target.value)}
                      placeholder="Project Alpha" data-testid="form-project-name"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </Field>
                  <Field label="OneDrive folder ID">
                    <input value={form.folder_id} onChange={e => set("folder_id", e.target.value)}
                      placeholder="Paste folder ID from Explore Files" data-testid="form-folder-id"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono text-xs" />
                    <p className="text-[10px] text-slate-400 mt-1">Find the folder ID in Explore Files (hover over the folder to see its ID in the URL)</p>
                  </Field>
                </>
              )}

              {isEmailReceived && (
                <>
                  <div className="bg-violet-50 border border-violet-100 rounded p-3 flex items-start gap-2">
                    <Info size={13} className="text-violet-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-violet-700 leading-relaxed">
                      This automation triggers whenever a new email arrives in your Outlook Inbox.
                      The email body (as HTML) and all attachments will be saved to the OneDrive folder you choose below.
                      It applies to your inbox globally — no project scope needed.
                    </p>
                  </div>
                  <Field label="Destination OneDrive folder (display name)">
                    <input value={form.destination_folder_name} onChange={e => set("destination_folder_name", e.target.value)}
                      placeholder="e.g. Inbox Archive" data-testid="form-dest-folder-name"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </Field>
                  <Field label="OneDrive folder ID" required>
                    <input value={form.destination_folder_id} onChange={e => set("destination_folder_id", e.target.value)}
                      placeholder="Paste folder ID from Explore Files" data-testid="form-dest-folder-id"
                      className="w-full text-sm border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono text-xs" />
                    <p className="text-[10px] text-slate-400 mt-1">Find the folder ID in Explore Files (hover over the folder to see its ID in the URL)</p>
                  </Field>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.only_with_attachments}
                      onChange={e => set("only_with_attachments", e.target.checked)}
                      className="w-3.5 h-3.5 accent-primary"
                      data-testid="form-only-attachments"
                    />
                    <span className="text-xs text-slate-700">Only trigger when the email has attachments</span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* Step 3 — scope */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Project Scope</p>
              <div className="space-y-2">
                {[
                  { v: "all", label: "All projects", desc: "Applies to all current and future projects" },
                  { v: "specific", label: "Specific projects", desc: "Choose which projects this applies to" },
                ].map(opt => (
                  <button key={opt.v} onClick={() => setScope(opt.v)}
                    className={`w-full text-left p-3 border rounded transition-colors ${scope === opt.v ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`scope-${opt.v}`}>
                    <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
              {scope === "specific" && (
                <div className="space-y-1.5 mt-2">
                  <p className="text-xs font-medium text-slate-600">Select projects:</p>
                  {projects.length === 0 ? (
                    <p className="text-xs text-slate-400">No projects found.</p>
                  ) : (
                    projects.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={selectedProjects.includes(p.id)}
                          onChange={e => setSelectedProjects(prev =>
                            e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id)
                          )}
                          className="w-3.5 h-3.5 accent-primary"
                        />
                        <span className="text-sm text-slate-700">{p.name}</span>
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 flex-shrink-0 bg-slate-50">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
            className="text-sm text-slate-500 hover:text-slate-700">
            {step === 1 ? "Cancel" : "Back"}
          </button>
          {step < totalSteps ? (
            <Button size="sm" onClick={() => setStep(s => s + 1)}
              disabled={step === 1 && !selectedTemplate}
              data-testid="wizard-next-btn">
              Next <CaretRight size={12} className="ml-1" />
            </Button>
          ) : (
            <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="wizard-create-btn">
              {saving ? <ArrowsClockwise size={12} className="animate-spin mr-1.5" /> : <Lightning size={12} className="mr-1.5" weight="fill" />}
              {saving ? "Creating…" : "Create Automation"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

/**
 * AutomationsContent — the AI automations UI without the page-level header.
 * Used by the Settings page to embed automations as a tab.
 */
export function AutomationsContent() {
  const { user } = useAuth();
  const userEmail = user?.email || "";
  const navigate  = useNavigate();

  const [automations, setAutomations]       = useState([]);
  const [connStatus, setConnStatus]         = useState({ outlook: {connected:false}, onedrive: {connected:false} });
  const [projects, setProjects]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showCreate, setShowCreate]         = useState(false);
  const [toggling, setToggling]             = useState(null);
  const [deleting, setDeleting]             = useState(null);
  const [saving, setSaving]                 = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState(null);

  // Confidence thresholds
  const [autoThresh, setAutoThresh]         = useState(0.85);
  const [pendThresh, setPendThresh]         = useState(0.25);
  const [threshSaving, setThreshSaving]     = useState(false);
  const [showThresholds, setShowThresholds] = useState(false);

  // Routing accuracy
  const [accuracyStats, setAccuracyStats]   = useState(null);

  // ── Load data ────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!userEmail) return;
    try {
      const [autos, projs, conn, thresholds] = await Promise.all([
        getAutomations(),
        getProjects(),
        getConnectorStatus(),
        getRouterThresholds().catch(() => ({ auto_route_threshold: 0.85, pending_threshold: 0.25 })),
      ]);
      setAutomations(autos);
      setProjects(projs);
      setConnStatus(conn);
      setAutoThresh(thresholds.auto_route_threshold);
      setPendThresh(thresholds.pending_threshold);
      // Load accuracy stats non-blocking
      getRoutingAccuracyStats(30).then(setAccuracyStats).catch(() => {});
    } catch (e) {
      toast.error("Failed to load automations.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const handleSaveThresholds = useCallback(async () => {
    if (pendThresh >= autoThresh) {
      toast.error("'Flag for review' threshold must be lower than 'Auto-route' threshold.");
      return;
    }
    setThreshSaving(true);
    try {
      await updateRouterThresholds({ auto_route_threshold: autoThresh, pending_threshold: pendThresh });
      toast.success("Confidence thresholds saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save thresholds.");
    } finally {
      setThreshSaving(false);
    }
  }, [autoThresh, pendThresh]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleToggle = useCallback(async (id) => {
    setToggling(id);
    try {
      const updated = await toggleAutomation(id);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, enabled: updated.enabled } : a));
      // Keep modal view in sync if open
      setSelectedAutomation(prev => (prev && prev.id === id) ? { ...prev, enabled: updated.enabled } : prev);
      toast.success(updated.enabled ? "Automation enabled." : "Automation disabled.");
    } catch (e) {
      toast.error("Could not toggle automation.");
    } finally {
      setToggling(null);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    setDeleting(id);
    try {
      await deleteAutomation(id);
      setAutomations(prev => prev.filter(a => a.id !== id));
      // Close the modal if the deleted automation was open
      setSelectedAutomation(prev => (prev && prev.id === id) ? null : prev);
      toast.success("Automation deleted.");
    } catch (e) {
      toast.error("Could not delete automation.");
    } finally {
      setDeleting(null);
    }
  }, []);

  const handleSave = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      const updated = await updateAutomation(id, payload);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, ...updated } : a));
      setSelectedAutomation(prev => (prev && prev.id === id) ? { ...prev, ...updated } : prev);
      toast.success("Automation updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update automation.");
      throw e;
    } finally {
      setSaving(false);
    }
  }, []);

  const activeCount = automations.filter(a => a.enabled).length;

  // ── Grouping for the three explicit sections ────────────────────────────────
  const { emailAutos, allProjectsAutos, projectSpecificAutos } = useMemo(() => {
    const emailAutos = [];
    const allProjectsAutos = [];
    const projectSpecificAutos = [];
    for (const a of automations) {
      if (a.template_type === "email_received_alert" || a.template_type === "email_project_router")
        emailAutos.push(a);
      else if (a.project_scope === "all") allProjectsAutos.push(a);
      else projectSpecificAutos.push(a);
    }
    return { emailAutos, allProjectsAutos, projectSpecificAutos };
  }, [automations]);

  return (
    <div className="space-y-6" data-testid="ai-automations-content">

      {/* New Automation button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(true)} data-testid="new-automation-btn"
          className="bg-primary text-white hover:bg-primary/90">
          <Plus size={13} className="mr-1.5" weight="bold" />
          New Automation
        </Button>
      </div>

        {/* AI Router Confidence Settings */}
        <div className="bg-white border border-slate-200" data-testid="router-confidence-settings">
          <button
            onClick={() => setShowThresholds(v => !v)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors"
            data-testid="confidence-settings-toggle"
          >
            <div className="flex items-center gap-2">
              <Sliders size={14} className="text-primary" weight="fill" />
              <span className="text-sm font-semibold text-slate-900">Email Router Confidence Settings</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-400">
                Auto-route ≥ {Math.round(autoThresh * 100)}% · Flag ≥ {Math.round(pendThresh * 100)}%
              </span>
              <CaretRight
                size={13}
                className={`text-slate-400 transition-transform duration-200 ${showThresholds ? "rotate-90" : ""}`}
              />
            </div>
          </button>

          {showThresholds && (
            <div className="px-5 pb-5 space-y-4 border-t border-slate-100">
              <p className="text-xs text-slate-500 pt-4">
                Controls how certain the AI must be before taking automatic action on an incoming email.
              </p>

              {/* Auto-route threshold */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-700">
                    Auto-route threshold
                    <span className="ml-1.5 text-[10px] font-normal text-slate-400">(email routed automatically)</span>
                  </label>
                  <span className="text-xs font-bold text-emerald-600 w-10 text-right" data-testid="auto-thresh-value">
                    {Math.round(autoThresh * 100)}%
                  </span>
                </div>
                <input
                  type="range" min="0.50" max="1.00" step="0.05"
                  value={autoThresh}
                  onChange={e => setAutoThresh(parseFloat(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                  data-testid="auto-thresh-slider"
                />
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>50% (permissive)</span>
                  <span>100% (strict)</span>
                </div>
              </div>

              {/* Pending threshold */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-slate-700">
                    Flag for review threshold
                    <span className="ml-1.5 text-[10px] font-normal text-slate-400">(below this → silently skipped)</span>
                  </label>
                  <span className="text-xs font-bold text-amber-600 w-10 text-right" data-testid="pend-thresh-value">
                    {Math.round(pendThresh * 100)}%
                  </span>
                </div>
                <input
                  type="range" min="0.05" max="0.50" step="0.05"
                  value={pendThresh}
                  onChange={e => setPendThresh(parseFloat(e.target.value))}
                  className="w-full accent-amber-500 cursor-pointer"
                  data-testid="pend-thresh-slider"
                />
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>5%</span>
                  <span>50%</span>
                </div>
              </div>

              {/* Visual band summary */}
              <div className="flex items-center gap-1 text-[10px] rounded overflow-hidden border border-slate-100">
                <div className="bg-slate-100 text-slate-400 px-2 py-1.5 flex-shrink-0">0%</div>
                <div className="bg-red-50 text-red-400 px-3 py-1.5 text-center"
                  style={{ flex: pendThresh }}>skip</div>
                <div className="bg-amber-50 text-amber-600 font-medium px-3 py-1.5 text-center"
                  style={{ flex: autoThresh - pendThresh }}>flag for review</div>
                <div className="bg-emerald-50 text-emerald-600 font-medium px-3 py-1.5 text-center"
                  style={{ flex: 1 - autoThresh }}>auto-route</div>
                <div className="bg-slate-100 text-slate-400 px-2 py-1.5 flex-shrink-0">100%</div>
              </div>

              {pendThresh >= autoThresh && (
                <p className="text-[11px] text-red-500 flex items-center gap-1">
                  <Warning size={12} /> Flag threshold must be lower than auto-route threshold.
                </p>
              )}

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveThresholds}
                  disabled={threshSaving || pendThresh >= autoThresh}
                  data-testid="save-thresholds-btn"
                >
                  {threshSaving
                    ? <ArrowsClockwise size={12} className="animate-spin mr-1.5" />
                    : <Sliders size={12} className="mr-1.5" />}
                  {threshSaving ? "Saving…" : "Save thresholds"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Email Routing Accuracy */}
        {accuracyStats && (
          <div className="bg-white border border-slate-200 px-5 py-4" data-testid="routing-accuracy-card">
            <div className="flex items-center gap-2 mb-3">
              <Target size={14} className="text-indigo-500" />
              <span className="text-sm font-semibold text-slate-900">Email Routing Accuracy</span>
              <span className="text-[10px] text-slate-400 ml-auto">Last 30 days</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Routed</p>
                <p className="font-cabinet text-2xl font-black text-slate-900">{accuracyStats.total_routed}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Auto-correct</p>
                <p className="font-cabinet text-2xl font-black text-emerald-600">{accuracyStats.auto_correct}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Overridden</p>
                <p className="font-cabinet text-2xl font-black text-orange-600">{accuracyStats.user_overridden}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Accuracy Rate</p>
                <p className="font-cabinet text-2xl font-black text-indigo-600">
                  {accuracyStats.accuracy_rate != null ? `${Math.round(accuracyStats.accuracy_rate * 100)}%` : "—"}
                </p>
              </div>
            </div>
            {accuracyStats.error_count > 0 && (
              <p className="text-[10px] text-red-500 mt-2">
                {accuracyStats.error_count} failed intake(s) — use the Replay button on affected items.
              </p>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "Total Automations", value: automations.length, testId: "stat-total" },
            { label: "Active",            value: activeCount,         testId: "stat-active" },
            { label: "Disabled",          value: automations.length - activeCount, testId: "stat-disabled" },
          ].map(({ label, value, testId }) => (
            <div key={label} data-testid={testId} className="bg-white border border-slate-200 px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
              <p className="font-cabinet text-3xl font-black tracking-tight text-slate-900 mt-1">{value}</p>
            </div>
          ))}
        </div>

        {/* Automation list */}
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-8 justify-center" data-testid="automations-loading">
            <ArrowsClockwise size={18} className="animate-spin" />
            <span className="text-sm">Loading automations…</span>
          </div>
        ) : automations.length === 0 ? (
          <div className="bg-white border border-slate-200 border-dashed py-16 text-center" data-testid="automations-empty">
            <Sparkle size={28} className="text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600">No automations yet</p>
            <p className="text-xs text-slate-400 mt-1 mb-4">Create your first automation to get started.</p>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="empty-new-btn">
              <Plus size={12} className="mr-1.5" /> New Automation
            </Button>
          </div>
        ) : (
          <div data-testid="automations-grouped">
            <AutomationsSection
              testId="section-email-automations"
              title="Email Automations"
              description="Triggered when a new email arrives in your Inbox"
              icon={Envelope}
              accent="text-violet-500"
              automations={emailAutos}
              onOpen={setSelectedAutomation}
              onToggle={handleToggle}
              onDelete={handleDelete}
              toggling={toggling}
              deleting={deleting}
              emptyHint="No email-triggered automations yet."
            />
            <AutomationsSection
              testId="section-all-projects-automations"
              title="Automations for All Projects"
              description="Apply across every current and future project"
              icon={Globe}
              accent="text-blue-500"
              automations={allProjectsAutos}
              onOpen={setSelectedAutomation}
              onToggle={handleToggle}
              onDelete={handleDelete}
              toggling={toggling}
              deleting={deleting}
              emptyHint="Nothing applies to all projects yet."
            />
            <AutomationsSection
              testId="section-project-specific-automations"
              title="Project-Specific Automations"
              description="Scoped to one or a subset of projects"
              icon={Target}
              accent="text-emerald-500"
              automations={projectSpecificAutos}
              onOpen={setSelectedAutomation}
              onToggle={handleToggle}
              onDelete={handleDelete}
              toggling={toggling}
              deleting={deleting}
              emptyHint="No project-specific automations yet."
            />
          </div>
        )}

      {showCreate && (
        <CreateModal
          connectorStatus={connStatus}
          projects={projects}
          userEmail={userEmail}
          onCreated={loadData}
          onClose={() => setShowCreate(false)}
          onNavigateToIntegrations={() => { setShowCreate(false); navigate("/settings?tab=integrations"); }}
        />
      )}

      {selectedAutomation && (
        <AutomationDetailModal
          automation={selectedAutomation}
          projects={projects}
          onSave={handleSave}
          onDelete={handleDelete}
          onToggle={handleToggle}
          onClose={() => setSelectedAutomation(null)}
          busy={
            saving
              ? "saving"
              : deleting === selectedAutomation.id
              ? "deleting"
              : toggling === selectedAutomation.id
              ? "toggling"
              : null
          }
        />
      )}
    </div>
  );
}

/**
 * Legacy route — redirects to Settings > Automations tab.
 * Kept so that bookmarks still work.
 */
export default function AIAutomationsPage() {
  const navigate = useNavigate();
  useEffect(() => { navigate("/settings?tab=automations", { replace: true }); }, [navigate]);
  return null;
}
